import React, {useCallback} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';

import Group from '@pb/kit-component-group';
import {Tooltip} from '@pb/kit-component-popups';
import Typography from '@pb/kit-component-typography';

import {IDP_PROVIDERS} from '../../constants';
import {useSsoSessionStorage} from '../../hooks/use-sso-session-storage';
import {StepFooter} from '../step-footer/step-footer';

import {ProviderCard} from './components/provider-card';

import {helperLink, helperText, providerGrid, selectIdpContainer} from './select-idp.module.styl';

type SelectIdpProps = {
    onContinue: () => void,
    onBack: () => void,
    saving?: boolean,
};

export const SelectIdp: React.FC<SelectIdpProps> = ({onContinue, onBack, saving}) => {
    const [selectedProvider, setSelectedProvider] = useSsoSessionStorage<string | null>('selectedProvider', null);
    const {idpCardSelect, idpChange} = useEventTracking();

    const handleSelect = useCallback((id: string) => {
        idpCardSelect({ssoIdpSelected: id});
        if (selectedProvider != null && selectedProvider !== id) {
            idpChange({fromIdp: selectedProvider, toIdp: id});
        }
        setSelectedProvider(id);
    }, [idpCardSelect, idpChange, selectedProvider, setSelectedProvider]);

    return (
        <Group direction="v" gap="l" className={selectIdpContainer}>
            <Group direction="v" gap="xs">
                <Typography variant="heading-level-1">
                    Choose Your Identity Provider
                </Typography>
                <Typography variant="body-level-1">
                    Choose the system your team uses to sign into company email or apps.
                </Typography>
            </Group>

            <Typography variant="body-level-1" className={helperText}>
                Not sure what to choose?{' '}
                <Tooltip
                    placement="bottom"
                    color="light"
                    anchor={({anchorRef, getAnchorProps}) => (
                        <a
                            className={helperLink}
                            ref={anchorRef}
                            {...getAnchorProps()}>
                            What&apos;s an Identity Provider?
                        </a>
                    )}>
                    <span>
                        Your Identity Provider is the system your
                        <br/>
                        company uses to sign in to email and apps,
                        <br/>
                        such as Google Workspace or Microsoft 365.
                    </span>
                </Tooltip>
            </Typography>

            <div className={providerGrid}>
                {IDP_PROVIDERS.map(provider => (
                    <ProviderCard
                        key={provider.id}
                        provider={provider}
                        selected={selectedProvider === provider.id}
                        onSelect={handleSelect}
                    />
                ))}
            </div>

            <StepFooter
                onContinue={onContinue}
                onBack={onBack}
                continueDisabled={selectedProvider == null}
                busy={saving}
            />
        </Group>
    );
};
